import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * "n combinations" — the tile hint that this dish sits in the order under more than one add-on
 * selection. The web analogue of iOS `SupplementCombinationsBadge`.
 *
 * It exists because the quantity badge beside it shows the SUM across those lines: two cheese
 * pizzas and one bacon pizza read as a single line of three otherwise, and from the tile alone the
 * guest has no way to know why `−` opens a sheet instead of just decrementing. Tapping this opens
 * exactly the picker `+` would.
 *
 * One component for both tiles rather than markup in each, for the same reason the routing rules
 * live in one file: the two tiles differ only in how much room they have, and a chip that says one
 * thing on the list and another on the strip is a chip nobody trusts.
 */
@Component({
    selector: 'app-supplement-combinations-badge',
    template: `
    <button type="button" class="combinations-badge" [class.compact]="compact"
            [attr.aria-label]="'addOns.combinations' | translate: { count: count }"
            (click)="onTap($event)">
      <svg class="combinations-icon" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M6 1 10.6 3.3 6 5.6 1.4 3.3 6 1Z" stroke="currentColor" stroke-width="1.1"
              stroke-linejoin="round" />
        <path d="M1.4 6.2 6 8.5 10.6 6.2" stroke="currentColor" stroke-width="1.1"
              stroke-linecap="round" stroke-linejoin="round" />
        <path d="M1.4 8.8 6 11.1 10.6 8.8" stroke="currentColor" stroke-width="1.1"
              stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <!-- Compact drops the wording, never the number: the narrow tile already carries a price and
           a stepper on one row, and "2 Kombinationen" does not fit beside them in the longer
           languages. The label survives on the accessible name either way. -->
      <span class="combinations-text">{{ compact ? count : ('addOns.combinations' | translate: { count: count }) }}</span>
    </button>
  `,
    styles: [`
    :host { display: inline-flex; }

    .combinations-badge {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 3px 7px;
      border: none;
      border-radius: 999px;
      /* Accent-tinted capsule: readable on both the card fill and the menu background, and it
         follows the venue's theme like everything else — ThemeService rewrites these. */
      background: rgba(var(--menu-accent-rgb, 255, 96, 100), 0.15);
      color: var(--menu-accent, #FF6064);
      font-family: inherit;
      font-size: 0.625rem;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
      cursor: pointer;
    }

    .combinations-badge.compact { padding: 3px 6px; }

    .combinations-icon {
      width: 10px;
      height: 10px;
      display: block;
      flex: none;
    }
  `],
    standalone: false
})
export class SupplementCombinationsBadgeComponent {
  /** How many distinct add-on selections of this product the guest is holding. */
  @Input() count = 0;

  /** Icon + number only, for the narrow tile. See the note on the label above. */
  @Input() compact = false;

  /**
   * Emitted with the original event so the host can stop it reaching the card body: the tile itself
   * opens the sheet in add mode, and a chip tap that bubbled would open it twice.
   */
  @Output() tapped = new EventEmitter<Event>();

  onTap(event: Event): void {
    this.tapped.emit(event);
  }
}
